import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { listTickets, type TicketListItem, type TicketStatus } from '../services/api/tickets';

export type TicketStatusFilter = TicketStatus | 'all';

const PAGE_SIZE = 20;

export const useTicketList = (initialStatusFilter: TicketStatusFilter = 'draft') => {
  const [statusFilter, setStatusFilterState] = useState<TicketStatusFilter>(initialStatusFilter);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [items, setItems] = useState<TicketListItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState<number | null>(null);

  const query = useQuery({
    queryKey: ['tickets', statusFilter, cursor ?? null],
    queryFn: () =>
      listTickets({
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: PAGE_SIZE,
        cursor,
      }),
  });

  useEffect(() => {
    if (!query.data) return;
    setItems((prev) => (cursor ? [...prev, ...query.data.items] : query.data.items));
    setHasMore(Boolean(query.data.nextCursor));
    setTotal(query.data.total);
    // cursor läses men får INTE stå i listan: effekten ska köra när svaret kommer,
    // inte när sidan begärs — annars läggs samma sida till en gång till.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data]);

  return {
    statusFilter,
    setStatusFilter: (next: TicketStatusFilter) => {
      setStatusFilterState(next);
      setCursor(undefined);
    },
    items,
    hasMore,
    total,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    loadMore: () => {
      const last = items[items.length - 1];
      if (last) setCursor(last.createdAt);
    },
    reset: () => setCursor(undefined),
  };
};
